import { apiClient } from "@/services/api"
import { hasMoreRows } from "@/services/listAnswer"
import { WAGE_TYPES } from "@/components/shared/employee/LewEmployeeEdit/wagetype"
import { useAuthStore } from "@/store/authentication"
import { defineStore } from "pinia"

let lastPage = -1

const _blankEmployee = () => {
  return {
    id: "",
    position: null,
    personnelNumber: "",
    personnelType: "",
    personnelKind: "",
    entryDate: "",
    exitDate: "",
    wageType: WAGE_TYPES.monthlyPay,
    wageValue: null,
    // Per-employee override of the tenant's employer's-contribution surcharge (Arbeitgeberanteil), in
    // percent; null means the tenant default applies.
    employerContributionPercent: null,
    totalHolidayDays: "",
    empowerment: "",
    socialInsuranceNumber: "",
    socialInsurance: "",
    createdAt: "",
    createdBy: "",
    lastModifiedAt: "",
    lastModifiedBy: "",
    birthdayDate: "",
    firstName: "",
    lastName: "",
    telephoneNumber: "",
    state: "",
    street: "",
    place: "",
    country: "",
    postcode: "",
    houseNumber: "",
    // The main language the mobile app shows itself in, a tag such as "sr-Latn"; null for the phone's
    language: null,
    grantedRoles: [],
    email: null,
    isEnabled: true,
    customFields: [],
    files: [],
    hasProfilePicture: false,
  }
}

export const useEmployeeStore = defineStore("employee", {
  state: () => ({
    employees: [],
    employee: _blankEmployee(),
  }),
  getters: {
    getEmployees: state => state.employees,
    getEmployee: state => state.employee,
  },
  actions: {
    setEmployees(employees) {
      this.employees = [...employees]
    },
    addEmployees(employees) {
      employees.forEach(employee => this.employees.push(employee))
    },
    clearEmployees() {
      this.employees = []
    },
    setEmployee(employee) {
      this.employee = employee
    },
    clearEmployee() {
      this.employee = _blankEmployee()
    },
    setEmployeeGrantedRoles(grantedRoles) {
      this.employee.grantedRoles = grantedRoles
    },
    loadEmployee(id) {
      return apiClient.get(`/user/${id}`).then(res => {
        this.setEmployee(res.data)
      })
    },
    // A management user has no login, so the API reports isEnabled: null for one. Echoing that null
    // back fails the whole save — the update endpoint takes the flag as a required boolean — so send
    // the default a fresh employee carries instead. The backend ignores it for users without a login.
    updateEmployee(employee) {
      return apiClient
        .put(`/user/${employee.id}`, { ...employee, isEnabled: employee.isEnabled ?? true })
        .then(res => {
          this.setEmployee(res.data)
        })
        .then(() => {
          const authStore = useAuthStore()
          if (employee.email == authStore.email) {
            authStore.loadPrincipal()
          }
        })
    },
    updateEmployeePassword({ employeeId, password }) {
      return apiClient.put(`/user/password/${employeeId}`, { password })
    },
    // Promotes a management user (no login) to a login-capable user. The backend preserves all existing
    // employee data and returns the updated user, which flips its derived type to WEB.
    convertToLoginUser({ employeeId, email, grantedRoles }) {
      return apiClient
        .post(`/user/${employeeId}/convert-to-login`, { email, grantedRoles })
        .then(res => {
          this.setEmployee(res.data)
          return res.data
        })
    },
    createEmployee(employee, files = []) {
      if (files && files.length > 0) {
        const formData = new FormData()
        formData.append('user', new Blob([JSON.stringify(employee)], { type: 'application/json' }))
        files.forEach(file => formData.append('files', file))
        return apiClient.post('/user/with-files', formData)
          .then(res => {
            this.setEmployee(res.data)
          })
          .then(() => this.getEmployee)
      }
      return apiClient
        .post("/user", employee)
        .then(res => {
          this.setEmployee(res.data)
        })
        .then(() => this.getEmployee)
    },
    loadEmployees({
      page = null,
      search = null,
      sortBy = null,
      sortDesc = null,
      selected = null,
      itemsPerPage = null,
      referenceType = undefined,
      referenceId = undefined,
      archive = undefined,
    }) {
      return apiClient
        .get("/user", {
          // `referenceType`/`referenceId` narrow the page to employees that point at one other
          // record; undefined when there is no such filter, and axios drops undefined params.
          params: { page, search, sortBy, sortDesc, selected, itemsPerPage, referenceType, referenceId, archive },
        })
        .then(res => {
          if (page === 0) {
            this.setEmployees(res.data.content)
          } else if (lastPage === page) {
            this.setEmployees(res.data.content)
          } else if (lastPage === page - 1) {
            this.addEmployees(res.data.content)
          } else {
            this.setEmployees(res.data.content)
          }
          lastPage = page
          return res
        })
    },
    /**
     * One page of employees for a search term, **returned** rather than written into `employees`.
     *
     * The store's list is what the Zeiterfassung screens read their pickers, their grids and their
     * "wer heißt eigentlich diese id" lookups from, all at once. A picker that searched through
     * `loadEmployees` would empty the grid beside it on every keystroke — so a search that is only
     * ever about one open dropdown keeps its answer to itself.
     *
     * Whether something did not fit is [hasMoreRows] — the endpoint answers with a `Page` whose total
     * is only the page's own size, so neither `last` nor the total can tell — and it is what the picker
     * turns into "verfeinern Sie die Suche".
     *
     * Silent: one error snackbar per keystroke over a field somebody is still typing in is worse
     * than a list that stays as it was.
     *
     * `page` for the pickers that load on as they are scrolled (`usePagedSearch`).
     */
    searchEmployees({ search, page = 0, itemsPerPage }) {
      return apiClient
        .get("/user", { params: { page, itemsPerPage, search: search || null }, silent: true })
        .then(res => ({ items: res.data?.content ?? [], hasMore: hasMoreRows(res.data, itemsPerPage) }))
    },
    findByName(firstName, lastName) {
      return apiClient
        .get("/user", {
          params: { search: `${firstName} ${lastName}`, itemsPerPage: 20, page: 0 },
        })
        .then(res => {
          const results = res.data.content ?? []
          return results.filter(
            e =>
              e.firstName?.toLowerCase() === firstName?.toLowerCase() &&
              e.lastName?.toLowerCase() === lastName?.toLowerCase()
          )
        })
    },
  },
})
